import apiClient from '@/api/client'
import axios from 'axios'
import { Money } from '@/shared/api/generated/critical-api'
import type { CartData, CartItem as GeneratedCartItem } from '@/shared/api/generated/critical-api'

/**
 * unit_price/unit_price_with_tax/tax_amount/total_price - Money-объект
 * {amount, currency} с бэкенда (CartService::serializeResolvedItem), не number.
 * PRO-100 уже поймала этот же drift на generated-контракте; здесь берём Money
 * из него вместо третьей ручной декларации. product.sku и minimum_order
 * бэкенд отдаёт, но их нет в critical-api.openapi.json - дополняем поверх.
 */
export interface CartResponseItem extends Omit<GeneratedCartItem, 'product'> {
  product: GeneratedCartItem['product'] & { sku: string | null }
  minimum_order?: number
}

/**
 * total_amount/tax_amount наследуются из CartData как есть - PRO-110: при их
 * изменении в OpenAPI это ручное объявление расходилось бы с контрактом молча,
 * type-check не заметил бы дрейф. Уточнения остаются только там, где generated-тип
 * слабее реального ответа: items - с sku/minimum_order, supplier - без Record<string, any>.
 */
export interface CartResponse extends Omit<CartData, 'items' | 'supplier'> {
  items: CartResponseItem[]
  supplier: {
    id: number
    name: string
  } | null
}

export interface CartSplitSupplier {
  supplier: {
    id: number
    name: string
  } | null
  items: CartResponseItem[]
  total_amount: Money
  tax_amount: Money
}

export interface CartSplitResponse {
  suppliers: CartSplitSupplier[]
  total_amount: Money
  tax_amount: Money
}

const ZERO_MONEY: Money = { amount: 0, currency: Money.currency.RUB }

const extractApiErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (axios.isAxiosError(error)) {
    const responseMessage = error.response?.data?.message
    if (typeof responseMessage === 'string' && responseMessage.trim() !== '') {
      return responseMessage
    }
  }

  if (error instanceof Error && error.message.trim() !== '') {
    return error.message
  }

  return fallbackMessage
}

export const cartApi = {
  async getCart(): Promise<CartResponse> {
    const { data } = await apiClient.get(`/api/cart`)
    return data?.data ?? { items: [], supplier: null, total_amount: ZERO_MONEY, tax_amount: ZERO_MONEY }
  },

  async addItem(productId: number | string, quantity: number): Promise<CartResponse> {
    const normalizedProductId =
      typeof productId === 'number' ? productId : Number.parseInt(String(productId), 10)

    if (!Number.isInteger(normalizedProductId) || normalizedProductId <= 0) {
      throw new Error('Invalid product_id')
    }

    try {
      const { data } = await apiClient.post(`/api/cart/items`, {
        product_id: normalizedProductId,
        quantity: Number(quantity),
      })
      return data?.data ?? { items: [], supplier: null, total_amount: ZERO_MONEY, tax_amount: ZERO_MONEY }
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Ошибка добавления товара в корзину'))
    }
  },

  async updateItem(productId: number | string, quantity: number): Promise<CartResponse> {
    try {
      const { data } = await apiClient.put(`/api/cart/items/${productId}`, {
        quantity: Number(quantity),
      })
      return data?.data ?? { items: [], supplier: null, total_amount: ZERO_MONEY, tax_amount: ZERO_MONEY }
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Ошибка обновления корзины'))
    }
  },

  async removeItem(productId: number | string): Promise<CartResponse> {
    try {
      const { data } = await apiClient.delete(`/api/cart/items/${productId}`)
      return data?.data ?? { items: [], supplier: null, total_amount: ZERO_MONEY, tax_amount: ZERO_MONEY }
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Ошибка удаления товара из корзины'))
    }
  },

  async clearCart(): Promise<CartResponse> {
    try {
      const { data } = await apiClient.delete(`/api/cart`)
      return data?.data ?? { items: [], supplier: null, total_amount: ZERO_MONEY, tax_amount: ZERO_MONEY }
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Ошибка очистки корзины'))
    }
  },

  async getSplitPreview(): Promise<CartSplitResponse> {
    const { data } = await apiClient.get(`/api/cart/split-preview`)
    return data?.data ?? { suppliers: [], total_amount: ZERO_MONEY, tax_amount: ZERO_MONEY }
  },
}
